/**
 * `/` — the front door.
 *
 * ── WHAT THIS PAGE MAY IMPORT ───────────────────────────────────────────────
 * NOTHING from `engine/match` or `engine/meta`, directly or transitively.
 * ADR-0005 splits the bundle shell -> meta -> match, and the landing page is
 * the definition of shell: it is what a cold visitor downloads before they have
 * decided whether to play. A static import of the match module here would put
 * ~230 KB gz of Pixi in front of that decision, and `vite.config.ts`'s
 * `bundleLayerGuard` would fail the build — which is the intended outcome, but
 * the rule is worth stating where it would be broken.
 *
 * ── WHY THERE IS NO PLAYER-COUNT COUNTER ────────────────────────────────────
 * `landing.stats_players` and `landing.stats_matches` exist in the catalogue
 * and are NOT rendered. There is no public stats endpoint in this branch —
 * `/api` exposes nothing that counts online players or matches played today.
 * A landing page that prints an invented number is a lie told to every visitor
 * on the most-read page of the site, so the section stays out until something
 * real feeds it. The strings can wait; a fabricated "1,204 players in the
 * arena" cannot be walked back.
 *
 * ── STRUCTURE ───────────────────────────────────────────────────────────────
 * One `<h1>`, then `<h2>` per section and `<h3>` per card, in that order, with
 * every section labelled by its own heading. That is not decoration: it is the
 * table of contents a screen-reader user navigates by, and it is the thing that
 * is expensive to retrofit once visual design lands on top.
 *
 * ── THE PICTURE SECTIONS, AND WHAT THEY COST ────────────────────────────────
 * `board()`, `towers()`, `roster()` and `cosmetics()` exist because the page
 * used to sell the game entirely in prose. They draw art that ALREADY SHIPS —
 * the base tower prop frames, the relit unit cut-outs, the shop's own cosmetic
 * tiles, one Seamhold plate — named in `game-facts.ts` and bound to the engine
 * by `landing-facts.test.ts`, so a renamed sprite or a rebalanced card fails a
 * test instead of quietly becoming a lie on the front page.
 *
 * They also add about 1.8 MB of imagery to a page with a three-second budget,
 * and the ONLY reason that is acceptable is that every one of those images is
 * below a full-height hero film and carries `loading="lazy"` — so first paint
 * is byte-for-byte what it was before they existed. `showcaseImage()` is where
 * that contract lives and `test/landing-showcase.test.ts` is what holds it,
 * with a byte ceiling that has already forced one tile back out. Anything added
 * here has to displace something, not join it.
 */
import { authStore, type AuthUser } from '../../core/state/auth-store';
import { audioPrefsStore, updateAudioPrefs, type AudioPrefs } from '../audio/prefs';
import { subscribeI18n, t } from '../i18n';
import { brandHeroLockup } from '../layout/brand';
import { TAP_TARGET, el, replaceChildren, type Child } from '../layout/dom';
import { ROUTES } from '../layout/routes';
import { createButton } from '../ui/button';
import { iconUrl, type UiIconId } from '../ui/icon';
import { slotUrl, type IconSlot } from '../ui/icon-registry';
import { ctaLink, decorateDisclosure } from './cta';
import { systemPrefersReducedMotion } from '../a11y/motion';
import { loadFaqPreview, peekFaqPreview, type FaqPreviewEntry } from './faq-preview';
import * as facts from './game-facts';
import { armReveal, ensureLandingStyles } from './landing-motion';

/**
 * The champions-clash hero film (Higgsfield-generated, baked from the 3D
 * models), for the landing hero. Plays once and HOLDS its last frame — a
 * `<video>` with no `loop` stops on the final frame — muted + playsinline so
 * mobile autoplays it; under reduced motion it never plays and the poster (that
 * same last frame) is all that shows. Built fresh per render, which is fine:
 * the landing re-renders only on locale/auth change, not on a timer.
 */
function heroFilm(soundToggle: HTMLElement): HTMLElement {
  const reduced = systemPrefersReducedMotion();
  const video = el('video', {
    class: 'absolute inset-0 h-full w-full object-cover',
    src: '/video/hero-fight.mp4',
    poster: '/video/hero-fight-poster.jpg',
    muted: true,
    playsinline: true,
    preload: 'metadata',
    'aria-hidden': 'true',
    tabindex: '-1',
  }) as HTMLVideoElement;
  video.muted = true; // the property, or autoplay is refused
  if (!reduced) {
    const start = (): void => { void video.play().catch(() => undefined); };
    if (video.readyState >= 2) start();
    else video.addEventListener('loadeddata', start, { once: true });
  }
  return el('div', {
    // FULL-SIZE hero film (owner ask): full-bleed, tall, edge to edge. It fills
    // most of the first screen under the sticky header; the text sits below it.
    class:
      'relative w-full overflow-hidden bg-bg ' +
      'h-[min(72vh,44rem)] min-h-[20rem]',
    'data-testid': 'landing-hero-film',
  }, [
    video,
    // A bottom-weighted scrim so the film fades into the page ground and the
    // text under it has a clean edge to sit against. It fades to the ground
    // TOKEN, not a typed colour: the film is a sunlit dusk now and the page
    // ground is whatever `--color-bg` says, so the seam follows the palette.
    el('div', {
      class: 'absolute inset-0',
      'aria-hidden': 'true',
      style:
        'background:linear-gradient(180deg,transparent 0%,transparent 58%,' +
        'color-mix(in srgb,var(--color-bg) 70%,transparent) 86%,var(--color-bg) 100%)',
    }),
    // The brand lockup CENTRED over the film (owner ask), above the scrim.
    // Decorative here — the sticky header already names the product — so it is
    // aria-hidden and not a link. Large: the hero is the front door.
    el('div', {
      class: 'pointer-events-none absolute inset-0 flex items-center justify-center px-4',
      'aria-hidden': 'true',
    }, [
      brandHeroLockup({ class: 'h-28 w-auto max-w-[90%] drop-shadow-lg sm:h-40 md:h-48' }),
    ]),
    // The sound on/off control, top-right of the first screen (owner bug: "no
    // toggle for the sound" on the landing page). Last child so it paints above
    // the scrim and the pointer-events-none lockup layer.
    soundToggle,
  ]);
}

/**
 * THE LANDING PAGE'S SOUND TOGGLE.
 *
 * The menu music bed (`engine/audio/manager.ts`) starts on a visitor's first
 * gesture anywhere, including on `/`, and the UI cues fire on every press — so
 * the front door makes sound, and until this existed the only way to silence
 * it was an account and the Settings screen.
 *
 * It drives THE SAME record as Settings' "Mute all sound" and the match HUD:
 * `audioPrefsStore.muted` (persisted as `rga.audio.muted`). There is no second
 * store. The manager subscribes to that store, so a press here takes the
 * playing bed to volume 0 on the same tick; and this button subscribes too, so
 * a mute set anywhere else is reflected here.
 *
 * A toggle button: the label is constant ("Mute sound") and `aria-pressed`
 * carries the state — pressed means muted. Built with the shared factory so
 * activation goes through `attachPress` (pointerup + keyboard click), never a
 * bare `click` listener.
 */
const SPEAKER_BODY = 'M4 9.5v5h3.5L12 18.5v-13L7.5 9.5H4z';
const SPEAKER_WAVES = 'M15.5 9.2a4 4 0 0 1 0 5.6 M18.3 6.6a7.6 7.6 0 0 1 0 10.8';
const SPEAKER_CROSS = 'M15.5 9.5l5 5 M20.5 9.5l-5 5';

export interface LandingSoundToggle {
  readonly root: HTMLButtonElement;
  destroy(): void;
}

export function createLandingSoundToggle(): LandingSoundToggle {
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '22');
  svg.setAttribute('height', '22');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  const body = document.createElementNS(NS, 'path');
  body.setAttribute('d', SPEAKER_BODY);
  body.setAttribute('fill', 'currentColor');
  const state = document.createElementNS(NS, 'path');
  state.setAttribute('stroke', 'currentColor');
  state.setAttribute('stroke-width', '1.8');
  state.setAttribute('stroke-linecap', 'round');
  svg.append(body, state);

  const label = t('landing.sound_mute');
  const button = createButton({
    content: [svg],
    ariaLabel: label,
    testId: 'landing-sound-toggle',
    class: 'absolute right-3 top-3 z-10 rounded-full shadow-lg sm:right-5 sm:top-5',
    onPress: () => updateAudioPrefs({ muted: !audioPrefsStore.get().muted }),
  });
  const root = button.root;
  root.title = label;

  const sync = (p: AudioPrefs): void => {
    root.setAttribute('aria-pressed', p.muted ? 'true' : 'false');
    root.dataset.muted = p.muted ? 'true' : 'false';
    state.setAttribute('d', p.muted ? SPEAKER_CROSS : SPEAKER_WAVES);
  };
  // Fires immediately, so the first paint already shows the stored state.
  const off = audioPrefsStore.subscribe(sync);

  return {
    root,
    destroy() {
      off();
      button.destroy();
    },
  };
}

const SECTION_CLASS = 'mx-auto w-full max-w-6xl px-4 py-10 sm:py-14';
const SECTION_HEADING_CLASS = 'mb-6 text-center font-heading text-2xl text-gold sm:text-3xl';
const CARD_CLASS = 'rga-lp-tile';
const CARD_HEADING_CLASS = 'font-heading text-base text-text sm:text-lg';
/** Layout only: the face, rim, press and focus ring are the shared skin's (`ctaLink`). */
const CTA_LAYOUT = 'px-6 font-bold';

/**
 * A picture for a tile: either painted art that ships in `public/ui/`
 * (`ui`) or a generated icon slot in `public/ui/slots/` (`slot`). Both are
 * small square PNGs; the page never draws the placeholder medallion here, since
 * every slot named below has its file in the build (`landing-showcase.test.ts`
 * resolves each one on disk).
 */
type Art = { readonly ui: UiIconId } | { readonly slot: IconSlot };

/** The four things a new player is told about the game, in catalogue order. */
const FEATURES: ReadonlyArray<{ readonly key: string; readonly art: Art }> = [
  { key: 'feature_lanes', art: { slot: 'landing-lanes' } },
  { key: 'feature_realtime', art: { slot: 'landing-realtime' } },
  { key: 'feature_deck', art: { ui: 'nav-cards' } },
  { key: 'feature_fair', art: { slot: 'landing-fair' } },
];
/** The three beats of a match. Numbered, hence an `<ol>`. */
const STEPS: ReadonlyArray<{ readonly key: string; readonly art: Art }> = [
  { key: 'how_step_1', art: { slot: 'landing-deploy' } },
  { key: 'how_step_2', art: { slot: 'landing-scout' } },
  { key: 'how_step_3', art: { slot: 'landing-sigils' } },
];
/**
 * The four factions, in the sim's own order. The accent is the faction colour
 * (a glow behind the emblem, never the only signal — the name carries it).
 * Names are proper nouns and read the same in every locale.
 */
const FACTIONS = [
  { key: 'faction_ember', color: '#e0642e', img: '/factions/ember.png' },
  { key: 'faction_hollow', color: '#8b6cff', img: '/factions/hollow.png' },
  { key: 'faction_thorn', color: '#5fbf6a', img: '/factions/thorn.png' },
  { key: 'faction_tide', color: '#3aa8d8', img: '/factions/tide.png' },
] as const;
/**
 * The ways to play: the FIVE `MatchModeKind`s the server actually runs, each
 * with the painted icon the game's own mode picker uses where one exists.
 */
const MODES: ReadonlyArray<{ readonly key: string; readonly art: Art }> = [
  { key: 'mode_ranked', art: { ui: 'sigil-rank-seal' } },
  { key: 'mode_training', art: { ui: 'mode-training' } },
  { key: 'mode_friendly', art: { ui: 'social-friends' } },
  { key: 'mode_draft', art: { ui: 'mode-draft' } },
  { key: 'mode_tournament', art: { ui: 'mode-tournament' } },
];

/**
 * THE MATCH, IN NUMBERS THE ENGINE AGREES WITH — a big figure and an icon per
 * fact. Every figure comes from `game-facts.ts`, which
 * `test/landing-facts.test.ts` binds to `@rga/sim`.
 */
const ANATOMY_STATS: ReadonlyArray<{ readonly key: string; readonly art: Art; readonly value: () => string }> = [
  { key: 'anatomy_lanes', art: { slot: 'landing-lanes' }, value: () => String(facts.LANES) },
  { key: 'anatomy_towers', art: { slot: 'landing-tower' }, value: () => String(facts.TOWERS_PER_SIDE) },
  { key: 'anatomy_clock', art: { ui: 'timer-hourglass' }, value: () => `${facts.REGULATION_MINUTES}:00` },
  { key: 'anatomy_mana', art: { ui: 'cost-pip-mana' }, value: () => String(facts.MANA_MAX) },
  { key: 'anatomy_deck', art: { ui: 'card-copies-stack' }, value: () => String(facts.DECK_SIZE) },
  { key: 'anatomy_tick', art: { slot: 'landing-realtime' }, value: () => `${facts.TICK_HZ} Hz` },
];

/** What the collection holds. Counts are engine-derived, not typed. */
const COLLECTION_STATS: ReadonlyArray<{ readonly key: string; readonly art: Art; readonly value: () => string }> = [
  { key: 'collection_cards', art: { ui: 'nav-cards' }, value: () => String(facts.CARD_TOTAL) },
  { key: 'collection_units', art: { ui: 'nav-battle' }, value: () => String(facts.CARD_UNITS) },
  { key: 'collection_spells', art: { slot: 'landing-spell' }, value: () => String(facts.CARD_SPELLS) },
  { key: 'collection_structures', art: { slot: 'landing-tower' }, value: () => String(facts.CARD_STRUCTURES) },
  { key: 'collection_rarities', art: { ui: 'rarity-epic-notched-gem' }, value: () => String(facts.RARITY_COUNT) },
  { key: 'collection_levels', art: { ui: 'card-level-up' }, value: () => String(facts.MAX_CARD_LEVEL) },
];

/** Where a season takes you. */
const PROGRESSION: ReadonlyArray<{ readonly key: string; readonly art: Art }> = [
  { key: 'progress_ladder', art: { slot: 'stat-trophy' } },
  { key: 'progress_warpath', art: { slot: 'goal-warpath' } },
  { key: 'progress_crates', art: { slot: 'nav-crates' } },
  { key: 'progress_houses', art: { ui: 'nav-house' } },
];

/*
 * ── LESS TEXT, MORE PICTURES (owner, 2026-10-14) ────────────────────────────
 * "On landing page, a lot of text, should be changed to images i think, or
 * icons generated added." Every section is now a grid of picture tiles: a
 * painted icon, a figure or a cut-out, then a two-to-five-word title and at
 * most one short line. The paragraphs that used to sit under each heading are
 * gone from the page; `/guide` still carries the full rules for the reader who
 * wants them, and the board section links there.
 *
 * What did NOT go: one `<h1>`, an `<h2>` per section and an `<h3>` per tile in
 * that order (the screen-reader table of contents), real `alt` on the two
 * informative plates, and the engine-bound numbers — now drawn large.
 */

/**
 * Render the landing page into `root` and return its teardown.
 *
 * Re-renders on language change and on sign-in/sign-out, and again when the
 * FAQ arrives. Everything renders synchronously first with whatever is cached,
 * so the page is never blank waiting on the network.
 */
export function renderLanding(root: HTMLElement): () => void {
  let destroyed = false;
  let faq: readonly FaqPreviewEntry[] = peekFaqPreview() ?? [];
  // The page is rebuilt on every locale/auth change; each build owns a fresh
  // toggle (its label is translated), so the previous one's store
  // subscription and press listeners are released first.
  let sound: LandingSoundToggle | null = null;
  let disarm: () => void = () => undefined;
  ensureLandingStyles(root.ownerDocument);

  const render = (): void => {
    if (destroyed) return;
    const user = authStore.get().user;
    sound?.destroy();
    sound = createLandingSoundToggle();
    disarm();
    // NO HEADER HERE: it is site chrome mounted around `#app` by
    // `engine/layout/chrome.ts`, so this screen renders only its own content.
    replaceChildren(
      root,
      el('main', { id: 'main', class: 'rga-landing flex flex-col' }, [
        // A fixed, decorative atmosphere behind the whole page. Inert and
        // aria-hidden; styled in app.css.
        el('div', { class: 'rga-landing__atmos', 'aria-hidden': 'true' }),
        hero(user, sound.root),
        features(),
        anatomy(),
        // The picture sections, in the order the game is learned: where you
        // fight, what you are shooting at, what you deploy, what you can wear.
        board(),
        towers(),
        roster(),
        factions(),
        collection(),
        howItWorks(),
        modes(),
        progression(),
        cosmetics(),
        faqSection(faq),
        finalCta(user),
      ]),
    );
    disarm = armReveal(root);
  };

  // Both `subscribeI18n` and `authStore.subscribe` invoke their listener
  // immediately, so the page is fully rendered before this function returns.
  const offI18n = subscribeI18n(() => render());
  const offAuth = authStore.subscribe(() => render());

  void loadFaqPreview().then((entries) => {
    if (destroyed) return;
    faq = entries;
    render();
  });

  return () => {
    destroyed = true;
    offI18n();
    offAuth();
    disarm();
    sound?.destroy();
    sound = null;
    replaceChildren(root);
  };
}

/* ═════════════════════════════════════════════════════════════ pieces ═══ */

/**
 * A tile's picture. Decorative (`alt=""`, aria-hidden): the `<h3>` beside it
 * names the thing, and an alt repeating the heading is noise in a screen
 * reader. Lazy, async, low priority and with its intrinsic box declared — the
 * same contract as `showcaseImage()` — and it floats gently while idle, each a
 * little out of step with its neighbours (`delay`).
 */
function artImg(art: Art, size: number, delay = 0): HTMLElement {
  const src = 'ui' in art ? iconUrl(art.ui) : slotUrl(art.slot);
  const intrinsic = 'ui' in art ? 128 : 256;
  return el('span', {
    class: 'rga-lp-art rga-lp-float',
    'aria-hidden': 'true',
    style: `width:${size}px;height:${size}px;animation-delay:${-delay * 0.7}s`,
  }, [
    el('img', {
      src,
      width: String(intrinsic),
      height: String(intrinsic),
      alt: '',
      loading: 'lazy',
      decoding: 'async',
      fetchpriority: 'low',
      draggable: 'false',
      style: `width:${size}px;height:${size}px`,
    }),
  ]);
}

/** A grid cell that fades and rises into view, a beat after the one before it. */
function revealItem(tag: 'li' | 'div', i: number, extra: string, children: Child[]): HTMLElement {
  return el(tag, {
    class: `${CARD_CLASS} rga-lp-reveal ${extra}`.trim(),
    style: `transition-delay:${Math.min(i, 6) * 70}ms`,
  }, children);
}

/** An icon + a two-to-five-word title. The commonest tile on the page. */
function iconTile(art: Art, title: string, i: number, size = 72, extra: Child[] = []): HTMLElement {
  return revealItem('li', i, '', [artImg(art, size, i), el('h3', { class: CARD_HEADING_CLASS, text: title }), ...extra]);
}

/** A big engine-bound figure with its icon and a short label. */
function statTile(art: Art, value: string, label: string, i: number): HTMLElement {
  return revealItem('li', i, '!gap-1.5 !py-4', [
    artImg(art, 56, i),
    // `div > span:first-child` is the figure: `landing-facts.test.ts` reads it.
    el('div', { class: 'flex flex-col items-center gap-1' }, [
      el('span', { class: 'rga-lp-figure', text: value }),
      el('span', { class: 'rga-lp-label', text: label }),
    ]),
  ]);
}

function heading(id: string, key: string): HTMLElement {
  return el('h2', { id, class: SECTION_HEADING_CLASS, text: t(key) });
}

/* ═══════════════════════════════════════════════════════════ sections ═══ */

/**
 * The hero. One `<h1>` for the whole document lives here.
 *
 * The primary call to action changes with the session rather than being
 * duplicated: a signed-in player wants "back to the arena", and offering them
 * "create an account" reads as a site that does not know who it is talking to.
 */
function hero(user: AuthUser | null, soundToggle: HTMLElement): HTMLElement {
  const primary = user
    ? ctaLink(document, { href: ROUTES.play, label: t('landing.cta_signed_in'), size: 'lg', icon: 'action-play', class: CTA_LAYOUT })
    : ctaLink(document, { href: ROUTES.register, label: t('landing.cta_primary'), size: 'lg', icon: 'action-play', class: CTA_LAYOUT });

  return el('section', { class: 'relative', 'aria-labelledby': 'landing-title' }, [
    // The champions-clash film — the first, full-screen thing a visitor sees.
    heroFilm(soundToggle),
    el('div', { class: 'mx-auto flex w-full max-w-6xl flex-col items-center px-4 pt-6 pb-10 text-center' }, [
      el('h1', {
        id: 'landing-title',
        class: 'font-heading text-4xl leading-tight text-gold sm:text-6xl',
        text: t('landing.hero_title'),
      }),
      el('div', { class: 'mt-6 flex flex-wrap justify-center gap-3' }, [
        primary,
        // In-page anchor. `main.ts` leaves hash links to the browser, so this is
        // a real fragment jump and not a router round trip.
        ctaLink(document, { href: '#how', label: t('landing.cta_secondary'), variant: 'secondary', size: 'lg', icon: 'action-help', class: CTA_LAYOUT }),
      ]),
      el('p', { class: 'mt-4 inline-flex items-center gap-2 text-sm text-text-dim' }, [
        artImg({ slot: 'landing-fair' }, 28),
        el('span', { text: t('landing.trust_note') }),
      ]),
    ]),
  ]);
}

/** Four claims, four pictures. A `<ul>`: an unordered set of equal claims. */
function features(): HTMLElement {
  return el('section', { class: SECTION_CLASS, 'aria-labelledby': 'landing-features' }, [
    heading('landing-features', 'landing.features_title'),
    el(
      'ul',
      { class: 'grid grid-cols-2 gap-4 lg:grid-cols-4' },
      FEATURES.map(({ key, art }, i) => iconTile(art, t(`landing.${key}.title`), i, 88)),
    ),
  ]);
}

/** The four factions: the emblem on a glow of the faction colour, and the name. */
function factions(): HTMLElement {
  return el('section', { class: SECTION_CLASS, 'aria-labelledby': 'landing-factions' }, [
    heading('landing-factions', 'landing.factions_title'),
    el(
      'ul',
      { class: 'grid grid-cols-2 gap-4 lg:grid-cols-4' },
      FACTIONS.map(({ key, color, img }, i) =>
        revealItem('li', i, 'overflow-hidden', [
          el('div', {
            class: 'flex w-full items-center justify-center rounded-lg p-2',
            style: `background:radial-gradient(circle at 50% 45%, ${color}55, transparent 70%)`,
          }, [
            el('span', { class: 'rga-lp-art rga-lp-float', style: `animation-delay:${-i * 0.7}s` }, [
              el('img', {
                class: 'h-28 w-28 object-contain',
                src: img,
                width: '112',
                height: '112',
                loading: 'lazy',
                decoding: 'async',
                alt: '',
              }),
            ]),
          ]),
          el('h3', { class: 'font-heading text-lg', style: `color:${color}`, text: t(`landing.${key}.title`) }),
        ]),
      ),
    ),
  ]);
}

/**
 * EVERY IMAGE IN THE FOUR PICTURE SECTIONS GOES THROUGH HERE.
 *
 *   width/height  the INTRINSIC pixel size, so the browser reserves the box
 *                 before a byte arrives (no reflow under the reader's thumb).
 *   loading=lazy  every one of these sections is below the full-height hero
 *                 film, so the FIRST load is unchanged by them.
 *   fetchpriority the images queue behind the hero video and the shell chunk.
 *     =low
 *   decoding      keeps a 512-wide PNG off the main thread.
 *     =async
 *
 * `test/landing-showcase.test.ts` holds each of these, and the byte budget.
 */
function showcaseImage(attrs: {
  readonly src: string;
  readonly w: number;
  readonly h: number;
  readonly alt: string;
  readonly class: string;
}): HTMLElement {
  return el('img', {
    class: attrs.class,
    src: attrs.src,
    width: String(attrs.w),
    height: String(attrs.h),
    loading: 'lazy',
    decoding: 'async',
    fetchpriority: 'low',
    alt: attrs.alt,
  });
}

/**
 * THE BOARD — one picture, three numbered markers, three titles.
 *
 * The dots on the plate are `aria-hidden` (a screen reader announcing "1 2 3"
 * over an image is a riddle); the `<ol>` under it carries the numbers for
 * assistive tech. The plate carries real `alt`: it is the only view of the
 * board on the site.
 */
function board(): HTMLElement {
  return el('section', { id: 'board', class: SECTION_CLASS, 'aria-labelledby': 'landing-board' }, [
    heading('landing-board', 'landing.board_title'),
    el('figure', { class: 'm-0' }, [
      el('div', { class: 'rga-lp-tile !p-0 relative mx-auto w-full max-w-[38rem] overflow-hidden' }, [
        showcaseImage({
          src: facts.BOARD_PLATE,
          w: facts.BOARD_PLATE_W,
          h: facts.BOARD_PLATE_H,
          alt: t('landing.board_alt'),
          class: 'block h-auto w-full',
        }),
        ...facts.BOARD_MARKS.map((mark, i) =>
          el('span', {
            class:
              'absolute flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center ' +
              'rounded-full bg-gold font-heading text-sm text-bg shadow-lg',
            'aria-hidden': 'true',
            style: `left:${mark.x}%;top:${mark.y}%`,
            text: String(i + 1),
          }),
        ),
      ]),
    ]),
    el(
      'ol',
      { class: 'mx-auto mt-6 grid max-w-[38rem] grid-cols-3 gap-3' },
      facts.BOARD_MARKS.map((mark, i) =>
        revealItem('li', i, '!py-3', [
          el('span', {
            class: 'flex h-8 w-8 items-center justify-center rounded-full bg-gold font-heading text-sm text-bg',
            'aria-hidden': 'true',
            text: String(i + 1),
          }),
          el('h3', { class: 'font-heading text-sm text-text sm:text-base', text: t(`landing.${mark.key}.title`) }),
        ]),
      ),
    ),
    // THE ONE LINK TO THE GUIDE: `/guide` explains a match end to end, so the
    // rules are not repeated here.
    el('div', { class: 'mt-6 flex justify-center' }, [
      ctaLink(document, { href: ROUTES.guide, label: t('landing.board_guide_link'), variant: 'ghost' }),
    ]),
  ]);
}

/**
 * THE THREE TOWERS, as the match draws them — the base prop frame (never a
 * skin a visitor does not own), the name, and the hit points at the level the
 * figure is anchored to, as a big number. The two Bastions are one tower in
 * the rules and two models in the art.
 */
function towers(): HTMLElement {
  return el('section', { id: 'towers', class: SECTION_CLASS, 'aria-labelledby': 'landing-towers' }, [
    heading('landing-towers', 'landing.towers_title'),
    el(
      'ul',
      { class: 'grid grid-cols-1 gap-4 sm:grid-cols-3' },
      facts.TOWERS.map((tower, i) => {
        const hp = tower.key === 'tower_keep' ? facts.KEEP_HP : facts.BASTION_HP;
        return revealItem('li', i, '', [
          el('div', {
            class: 'flex h-40 w-full items-center justify-center rounded-lg',
            style: 'background:radial-gradient(circle at 50% 45%, rgba(242,193,78,.22), transparent 70%)',
          }, [
            showcaseImage({
              src: tower.img,
              w: tower.w,
              h: tower.h,
              alt: '',
              class: 'max-h-36 w-auto object-contain drop-shadow-[0_14px_24px_rgba(0,0,0,0.55)]',
            }),
          ]),
          el('h3', { class: CARD_HEADING_CLASS, text: t(`landing.${tower.key}.title`) }),
          el('p', { class: 'flex flex-wrap items-baseline justify-center gap-x-2' }, [
            el('span', { class: 'rga-lp-figure', text: String(hp) }),
            el('span', { class: 'rga-lp-label', text: `${t('decks.stat_hp')} · ${t('decks.card_level', { level: facts.TOWER_LEVEL })}` }),
          ]),
        ]);
      }),
    ),
  ]);
}

/**
 * THE UNITS — eight of the forty, two under each banner: the cut-out, the
 * name, the mana cost as a badge and the hit points (and the body count, for
 * a swarm). Cut-out always ABOVE the text: beside it, at 320 px, the card ran
 * to fourteen lines and put the badge a pixel past the document edge.
 */
function roster(): HTMLElement {
  return el('section', { id: 'roster', class: SECTION_CLASS, 'aria-labelledby': 'landing-roster' }, [
    heading('landing-roster', 'landing.roster_title'),
    el(
      'ul',
      { class: 'grid grid-cols-2 gap-4 lg:grid-cols-4' },
      facts.ROSTER.map((unit, i) =>
        revealItem('li', i, '', [
          el('div', {
            class: 'flex h-32 w-full items-end justify-center rounded-lg sm:h-40',
            style: `background:radial-gradient(circle at 50% 35%, ${unit.color}44, transparent 72%)`,
          }, [
            showcaseImage({
              src: unit.img,
              w: unit.w,
              h: unit.h,
              alt: '',
              class: 'max-h-full w-auto object-contain drop-shadow-[0_14px_24px_rgba(0,0,0,0.5)]',
            }),
          ]),
          // `flex-wrap` and the badge's `shrink-0`: a long name and a badge on
          // one unwrappable row once gave the page a horizontal scrollbar.
          el('div', { class: 'flex flex-wrap items-center justify-center gap-2' }, [
            el('h3', { class: CARD_HEADING_CLASS, text: t(`landing.${unit.key}.title`) }),
            el('span', {
              class: 'flex h-7 min-w-7 shrink-0 items-center justify-center rounded-full bg-gold px-1.5 font-heading text-sm text-bg',
              title: t('decks.card_cost', { cost: unit.cost }),
              text: String(unit.cost),
            }),
          ]),
          el('p', {
            class: 'rga-lp-label',
            text:
              `${t('decks.stat_hp')} ${unit.hp}` +
              (unit.count > 1 ? ` · ${t('decks.stat_count_value', { count: unit.count })}` : ''),
          }),
        ]),
      ),
    ),
    el('p', { class: 'mt-4 text-center text-sm text-text-dim', text: t('landing.roster_neutral') }),
  ]);
}

/**
 * WHAT YOU CAN WEAR — five cosmetic kinds, as the shop's own tiles: the
 * picture, the shelf it sits on and the item's name. Every string is the
 * wardrobe's own (`wardrobe.kind.*`, `cosmetics.<kind>.<slug>.name`), built
 * from template literals so `i18n-completeness` sees them as dynamic keys.
 *
 * A horizontal snap rail below `sm` (one and a half tiles fetched, not five),
 * a grid above it.
 */
function cosmetics(): HTMLElement {
  return el('section', { id: 'cosmetics', class: SECTION_CLASS, 'aria-labelledby': 'landing-cosmetics' }, [
    heading('landing-cosmetics', 'landing.cosmetics_title'),
    el(
      'ul',
      {
        class:
          'flex snap-x gap-4 overflow-x-auto pb-2 ' +
          'sm:grid sm:grid-cols-2 sm:overflow-visible sm:pb-0 lg:grid-cols-3',
      },
      facts.COSMETIC_SHOWCASE.map((item, i) => {
        // `deploy-vfx` is `deploy_vfx` under `wardrobe.*`. Not imported from
        // `wardrobe-api.ts`: that pulls the network layer into the shell chunk.
        const segment = item.kind.replace(/-/g, '_');
        return revealItem('li', i, 'w-64 shrink-0 snap-start !p-3 sm:w-auto', [
          showcaseImage({
            src: `/cosmetics/cosmetic-${item.kind}-${item.slug}.png`,
            w: facts.COSMETIC_TILE_W,
            h: facts.COSMETIC_TILE_H,
            alt: '',
            class: 'block h-auto w-full rounded-lg',
          }),
          el('p', { class: 'text-xs uppercase tracking-wide text-gold', text: t(`wardrobe.kind.${segment}`) }),
          el('h3', { class: CARD_HEADING_CLASS, text: t(`cosmetics.${item.kind}.${item.slug}.name`) }),
        ]);
      }),
    ),
  ]);
}

/** The ways to play: five painted icons and five names. */
function modes(): HTMLElement {
  return el('section', { class: SECTION_CLASS, 'aria-labelledby': 'landing-modes' }, [
    heading('landing-modes', 'landing.modes_title'),
    el(
      'ul',
      { class: 'grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5' },
      MODES.map(({ key, art }, i) => iconTile(art, t(`landing.${key}.title`), i, 72)),
    ),
  ]);
}

/**
 * HOW A MATCH GOES — an illustrated 1-2-3 strip. An `<ol>`, because the order
 * is the content; the gold badge carries the number visually and is
 * `aria-hidden`, the list numbers it for assistive tech (and for RTL).
 */
function howItWorks(): HTMLElement {
  return el('section', { id: 'how', class: SECTION_CLASS, 'aria-labelledby': 'landing-how' }, [
    heading('landing-how', 'landing.how_title'),
    el(
      'ol',
      { class: 'rga-lp-steps grid grid-cols-1 gap-6 sm:grid-cols-3' },
      STEPS.map(({ key, art }, i) =>
        revealItem('li', i, '!pt-6', [
          el('span', { class: 'rga-lp-badge', 'aria-hidden': 'true', text: String(i + 1) }),
          artImg(art, 104, i),
          el('h3', { class: CARD_HEADING_CLASS, text: t(`landing.${key}.title`) }),
        ]),
      ),
    ),
  ]);
}

/** ANATOMY OF A MATCH — six facts, each a big number the engine agrees with. */
function anatomy(): HTMLElement {
  return el('section', { id: 'anatomy', class: SECTION_CLASS, 'aria-labelledby': 'landing-anatomy' }, [
    heading('landing-anatomy', 'landing.anatomy_title'),
    el(
      'ul',
      { class: 'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6' },
      ANATOMY_STATS.map(({ key, art, value }, i) => statTile(art, value(), t(`landing.${key}`), i)),
    ),
  ]);
}

/** WHAT IS IN THE GAME — the collection, counted rather than described. */
function collection(): HTMLElement {
  return el('section', { class: SECTION_CLASS, 'aria-labelledby': 'landing-collection' }, [
    heading('landing-collection', 'landing.collection_title'),
    el(
      'ul',
      { class: 'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6' },
      COLLECTION_STATS.map(({ key, art, value }, i) => statTile(art, value(), t(`landing.${key}`), i)),
    ),
  ]);
}

/**
 * WHERE A SEASON TAKES YOU — the finished Seamhold, then four picture tiles.
 *
 * The crate tile LINKS TO THE PUBLISHED ODDS. §4 requires the odds to be
 * readable without an account; mentioning crates on the front page without
 * that link would describe a paid random draw and hide the numbers.
 */
function progression(): HTMLElement {
  return el('section', { class: SECTION_CLASS, 'aria-labelledby': 'landing-progress' }, [
    heading('landing-progress', 'landing.progress_title'),
    // THE SEAMHOLD, at its last stage: "raise the Seamhold together" means
    // nothing until you have seen it. 250 KB of WebP, the cheapest plate here.
    el('figure', { class: 'm-0 mb-6' }, [
      showcaseImage({
        src: facts.SEAMHOLD_PLATE,
        w: facts.SEAMHOLD_PLATE_W,
        h: facts.SEAMHOLD_PLATE_H,
        alt: t('landing.houses_alt'),
        class: 'rga-lp-tile !p-0 block h-auto w-full',
      }),
    ]),
    el(
      'ul',
      { class: 'grid grid-cols-2 gap-4 lg:grid-cols-4' },
      PROGRESSION.map(({ key, art }, i) =>
        iconTile(
          art,
          t(`landing.${key}.title`),
          i,
          72,
          key !== 'progress_crates'
            ? []
            : [
                // The literal, not `ODDS_PATH` from `engine/meta/route`: this
                // page may not import from `engine/meta` (ADR-0005).
                ctaLink(document, { href: '/crates/odds', label: t('landing.progress_odds_link'), variant: 'ghost', size: 'sm' }),
              ],
        ),
      ),
    ),
  ]);
}

/**
 * A few real answers, then the way to the rest. `<details>` rather than a
 * scripted accordion: keyboard operable, announced correctly, and works before
 * JavaScript has done anything else. With nothing from the CMS, only the link.
 */
function faqSection(entries: readonly FaqPreviewEntry[]): HTMLElement {
  const children: Child[] = [heading('landing-faq', 'landing.faq_title')];

  if (entries.length > 0) {
    children.push(
      el(
        'ul',
        { class: 'mx-auto mb-6 flex max-w-3xl flex-col gap-2' },
        entries.map((entry) =>
          el('li', {}, [
            // The WHOLE box toggles, not just the words.
            el('details', { class: 'rga-lp-tile !block !p-0 !text-left overflow-hidden' }, [
              decorateDisclosure(el('summary', {
                class:
                  `${TAP_TARGET} w-full cursor-pointer select-none px-5 py-3 ` +
                  'font-heading text-text hover:bg-surface-2',
                text: entry.question,
              })),
              el('p', {
                class: 'whitespace-pre-line px-5 pb-4 text-text-dim',
                text: entry.answer,
              }),
            ]),
          ]),
        ),
      ),
    );
  }

  children.push(
    el('div', { class: 'flex justify-center' }, [
      ctaLink(document, { href: ROUTES.faq, label: t('landing.faq_cta'), variant: 'ghost', icon: 'action-next', iconEnd: true }),
    ]),
  );

  return el('section', { class: SECTION_CLASS, 'aria-labelledby': 'landing-faq' }, children);
}

/**
 * The closing ask. Hidden for a signed-in player — they already have an
 * account, and "create an account" at the bottom would ignore their session.
 */
function finalCta(user: AuthUser | null): HTMLElement | null {
  if (user) return null;
  return el('section', { class: SECTION_CLASS, 'aria-labelledby': 'landing-final' }, [
    el('div', { class: `${CARD_CLASS} rga-lp-reveal !gap-4 !py-8` }, [
      artImg({ ui: 'reward-bundle' }, 96),
      el('h2', {
        id: 'landing-final',
        class: 'font-heading text-2xl text-gold',
        text: t('landing.final_cta_title'),
      }),
      ctaLink(document, { href: ROUTES.register, label: t('landing.final_cta_button'), size: 'lg', icon: 'action-play', class: CTA_LAYOUT }),
    ]),
  ]);
}
